// Abandoned-cart reminder emails link to
// /v3/cart?abandoned_cart_notification_id=... (never a cart uuid; links sent
// before that change also carry cart_uuid, which is kept only to be checked
// against the restored cart), but /v3/cart is
// behind an auth gate that redirects logged-out visitors to /login-redirect,
// which pushes /login and hard-reloads the page. The login forms send a
// completed login to /dashboard, so without help the query string is gone by
// the time the parent could reach the cart.
//
// So the two params are stashed in sessionStorage at the cart route, *before*
// the auth gate can redirect. sessionStorage survives the hard reload in the
// same tab; the login forms send the parent back to /v3/cart when a stash is
// present, and the cart route falls back to the stash when it mounts with a
// bare URL. With no stash, every login behaves exactly as before.

export interface AbandonedCartAttribution {
  cartUuid: string | null;
  abandonedCartNotificationId: string | null;
}

interface StoredAttribution extends AbandonedCartAttribution {
  returnPath: typeof REMINDER_RETURN_PATH | null;
}

const STORAGE_KEY = 'pics.abandonedCartAttribution';
const REMINDER_RETURN_PATH = '/v3/cart';

const EMPTY: StoredAttribution = {
  cartUuid: null,
  abandonedCartNotificationId: null,
  returnPath: null,
};

// sessionStorage throws in Safari private mode and in sandboxed iframes.
// Attribution is best-effort and must never break the cart or login.
const runSafely = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

const parseSearch = (search?: string | null): AbandonedCartAttribution => {
  const params = new URLSearchParams(search || '');

  return {
    cartUuid: params.get('cart_uuid') || null,
    abandonedCartNotificationId:
      params.get('abandoned_cart_notification_id') || null,
  };
};

const hasAttribution = (attribution: AbandonedCartAttribution): boolean =>
  Boolean(attribution.cartUuid || attribution.abandonedCartNotificationId);

const writeStored = (attribution: Partial<StoredAttribution>): void =>
  runSafely(
    () =>
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution)),
    undefined,
  );

const clearStored = (): void =>
  runSafely(() => window.sessionStorage.removeItem(STORAGE_KEY), undefined);

const readStored = (): StoredAttribution =>
  runSafely(() => {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;

    const parsed = JSON.parse(raw) || {};

    return {
      cartUuid: parsed.cartUuid || null,
      abandonedCartNotificationId: parsed.abandonedCartNotificationId || null,
      returnPath:
        parsed.returnPath === REMINDER_RETURN_PATH
          ? REMINDER_RETURN_PATH
          : null,
    };
  }, EMPTY);

// Called from the /v3/cart route render, ahead of the auth gate. A no-op when
// the URL carries neither param, so ordinary cart visits never write anything.
export const captureAbandonedCartAttribution = (
  search?: string | null,
): AbandonedCartAttribution => {
  const attribution = parseSearch(search);
  if (!hasAttribution(attribution)) return EMPTY;

  writeStored({ ...attribution, returnPath: REMINDER_RETURN_PATH });

  return attribution;
};

// The query string wins when present; otherwise fall back to whatever the
// pre-auth capture stashed. With `consume` the stash is cleared, so a later,
// unrelated cart visit in the same tab is not misattributed.
export const resolveAbandonedCartAttribution = (
  search?: string | null,
  { consume = true }: { consume?: boolean } = {},
): AbandonedCartAttribution => {
  const fromSearch = parseSearch(search);

  if (hasAttribution(fromSearch)) {
    if (consume) clearStored();
    return fromSearch;
  }

  const stored = readStored();
  if (consume && hasAttribution(stored)) clearStored();

  return {
    cartUuid: stored.cartUuid,
    abandonedCartNotificationId: stored.abandonedCartNotificationId,
  };
};

// Set once this document has handed a login form the reminder return path.
// A form can have two callers race for one login (its submit handler and its
// auth effect); both must get the same answer rather than one sending the
// parent to /v3/cart and the other to /dashboard. The caller navigates with a
// full page load, which resets this.
let returnPathConsumed = false;

// Where a login form should send a parent who arrived from a reminder link:
// always the fixed cart path, never a URL copied from caller input, and null
// when there is no stash (the form then does exactly what it did before).
// Consumes only the return-path marker; the cart route consumes the paired
// attribution once it has restored the cart.
export const consumeAbandonedCartReturnPath = ():
  | typeof REMINDER_RETURN_PATH
  | null => {
  if (returnPathConsumed) return REMINDER_RETURN_PATH;

  const stored = readStored();

  if (!hasAttribution(stored) || stored.returnPath !== REMINDER_RETURN_PATH) {
    return null;
  }

  writeStored({
    cartUuid: stored.cartUuid,
    abandonedCartNotificationId: stored.abandonedCartNotificationId,
  });
  returnPathConsumed = true;

  return REMINDER_RETURN_PATH;
};

// For a login form that has just completed a login: when the parent arrived
// from a reminder link, navigate (with a full page load) to its cart and return
// true; otherwise return false and do nothing, so the form carries on with the
// destination it always had.
export const returnToAbandonedCartAfterLogin = (
  navigate: (path: string) => void = (path) => window.location.assign(path),
): boolean => {
  const reminderPath = consumeAbandonedCartReturnPath();
  if (!reminderPath) return false;

  navigate(reminderPath);
  return true;
};

// Test-only: the flag above lives for the document, and a test file is one.
export const resetAbandonedCartReturnPathForTests = (): void => {
  returnPathConsumed = false;
};

export const ABANDONED_CART_ATTRIBUTION_STORAGE_KEY = STORAGE_KEY;
